class SideBySideShop extends HTMLElement {
    constructor() {
        super();
        this.sidebtsideJsonData = null;
        this.uniqueMakeModels = [];
        this.matchingUniqueYears = [];
        this.elements = {};
        this.selectedData = {
            make: '',
            model: '',
            year: ''
        };
        this.initJsonData();
    }

    async initJsonData() {
        try {
            const json = await vehicleUtility.tsvsideJSON();
            this.initVehicle(json);
        } catch (error) {
            console.error('Failed to load vehicle data:', error);
        }
    }

    initVehicle(json) {
        this.sidebtsideJsonData = json;
        this.uniqueMakeModels = this.getUniqueMakeModels();
        this.cacheElements();
        this.bindEvents();

        const limitedSuggestions = this.uniqueMakeModels.slice(0, 3);

        const container = this.querySelector("[data-vehicle-suggestion]");
        const suggestionBox = this.querySelector(`[data-vehicle-suggestion-box]`);

        if (!container || !suggestionBox) return;

        this.showElement(suggestionBox);
        container.innerHTML = '';

        limitedSuggestions.forEach(item => {
            const formattedText = item.replace(/\s?\|\s?/g, ' ');
            
            const li = this.createElement('li', formattedText, {
                'data-vehicle-value': '',
                'data-vehicle-model-value': item,
                class: 'block__vehicle-lookup-input-result-li'
            });

            li.addEventListener('click', (e) => {
                container.querySelectorAll("li").forEach((list) => {
                    list.classList.remove("selected");
                });
                e.target.classList.add("selected");
                let targetMakeModel = e.target.getAttribute("data-vehicle-model-value");
                var makeModelValue = e.target.textContent;

                const [make, model] = targetMakeModel.split('|').map(v => v.trim());
                this.selectedData.make = make;
                this.selectedData.model = model;
                this.matchingUniqueYears = this.getUniqueYears(make, model);

                e.target.closest(".block__vehicle-lookup-input-box").querySelector('[data-search-vehicle-make-model]').value = makeModelValue;
                e.target.closest(".block__vehicle-lookup-input-box").querySelector('[data-search-vehicle-submit]').classList.remove("d-none");
            });
            container.appendChild(li);
        });
    }

    getUniqueMakeModels() {
        return [...new Set(Object.values(this.sidebtsideJsonData)
            .map(item => `${item.make} | ${item.model}`)
            .filter(Boolean))];
    }

    cacheElements() {
        const selectors = {
            makeModelInput: '[data-search-vehicle-make-model]',
            makeModelList: '[data-search-vehicle-make-model-list]',
            makeModelBox: '[data-selected-vehicle-make-model]',
            submitButton: '[data-search-vehicle-submit]',
            yearInput: '[data-search-vehicle-year]',
            yearList: '[data-search-vehicle-year-list]',
            yearBox: '[data-selected-vehicle-year]',
            yearSubmitButton: '[data-search-vehicle-year-submit]'
        };

        Object.entries(selectors).forEach(([key, selector]) => {
            this.elements[key] = this.querySelector(selector);
        });
    }

    bindEvents() {
        if (!this.elements.makeModelInput) return;

        this.bindSearchEvents();
        this.bindStepBackEvents();
    }

    bindSearchEvents() {
        const searchConfigs = [
            {
                input: this.elements.makeModelInput,
                list: this.elements.makeModelList,
                box: this.elements.makeModelBox,
                submit: this.elements.submitButton,
                type: 'makeModel',
                minLength: 3,
                handler: (e) => this.handleSearch(e, 'makeModel'),
                selectionHandler: (e) => this.handleSelection(e, 'makeModel'),
                submitHandler: (e) => this.handleSubmit(e, 'makeModel')
            },
            {
                input: this.elements.yearInput,
                list: this.elements.yearList,
                box: this.elements.yearBox,
                submit: this.elements.yearSubmitButton,
                type: 'year',
                minLength: 3,
                handler: (e) => this.handleSearch(e, 'year'),
                selectionHandler: (e) => this.handleSelection(e, 'year'),
                submitHandler: (e) => this.handleSubmit(e, 'year')
            }
        ];

        searchConfigs.forEach(config => this.bindSearchConfig(config));
    }

    bindSearchConfig(config) {
        if (!config.input) return;

        let debounceTimer;
        config.input.addEventListener('keyup', (e) => {
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => config.handler(e), 150);
        });

        config.list?.addEventListener('click', config.selectionHandler);
        config.submit?.addEventListener('click', config.submitHandler);
    }

    bindStepBackEvents() {
        document.addEventListener('click', (e) => {
            const backButton = e.target.closest('[data-step-back-btn]');
            if (!backButton) return;

            e.preventDefault();
            const backStep = backButton.getAttribute('data-back-step');
            
            if (backStep && backStep !== '0') {
                this.navigateToPreviousStep(backButton, backStep);
            } else {
                backButton.closest('.block__vehicle-lookup-popup').classList.add('d-none');
            }
        });
    }

    navigateToPreviousStep(button, backStep) {
        const currentStep = button.closest('.block__vehicle-lookup-popup-details');
        const parentEle = currentStep.closest('.block__vehicle-lookup-popup-list');
        
        parentEle.querySelectorAll('.block__vehicle-lookup-popup-details')
            .forEach(step => this.hideElement(step));
            
        const previousStep = parentEle.querySelector(`[current-step="${backStep}"]`);
        if (previousStep) this.showElement(previousStep);
    }

    handleSearch(e, type) {
        const query = this.normalizeQuery(e.target.value);
        const config = this.getSearchConfig(type);
        const inputBox = e.target.closest('.block__vehicle-lookup-input-box');
        
        // Toggle active state
        inputBox?.classList.toggle('active-value', !!query);
        
        this.toggleSubmitButton(config.submit, false);
        config.list.innerHTML = '';

        if (!query) {
            this.hideElement(config.box);
            return;
        }

        const minLength = type === 'makeModel' ? 3 : 3;
        if (query.length >= minLength) {
            const matches = this.getMatches(type, query);
            this.populateResults(config.list, config.box, matches, type);
        } else {
            this.showMinLengthMessage(config.list, config.box, minLength, type);
        }
    }

    getSearchConfig(type) {
        const configs = {
            makeModel: {
                list: this.elements.makeModelList,
                box: this.elements.makeModelBox,
                submit: this.elements.submitButton
            },
            year: {
                list: this.elements.yearList,
                box: this.elements.yearBox,
                submit: this.elements.yearSubmitButton
            }
        };
        return configs[type];
    }

    getMatches(type, query) {
        switch (type) {
            case 'makeModel':
                return this.filterMakeModels(query);
            case 'year':
                // Filter matching years and sort newest first
                return this.matchingUniqueYears
                    .filter(year => String(year).toLowerCase().includes(query))
                    .sort((a, b) => parseInt(b) - parseInt(a)); // Sort descending (newest first)
            default:
                return [];
        }
    }

    handleSelection(e, type) {
        const attributeMap = {
            makeModel: 'data-vehicle-model-value',
            year: 'data-vehicle-year-value'
        };

        const attribute = attributeMap[type];
        if (!e.target.matches(`[${attribute}]`)) return;

        const inputBox = e.target.closest('.block__vehicle-lookup-input-box');
        inputBox?.classList.remove('active-value');

        const selectedValue = e.target.getAttribute(attribute)?.trim();
        if (!selectedValue) return;

        this.updateSelectedData(type, selectedValue);
        this.updateInputAndUI(e.target, this.getDisplayValue(type, selectedValue), type);
    }

    updateSelectedData(type, value) {
        if (type === 'makeModel') {
            const [make, model] = value.split('|').map(v => v.trim());
            this.selectedData.make = make;
            this.selectedData.model = model;
            this.matchingUniqueYears = this.getUniqueYears(make, model);
        } else if (type === 'year') {
            this.selectedData.year = value;
        }
    }

    getDisplayValue(type, value) {
        return type === 'makeModel' 
            ? value.replace(/\s*\|\s*/g, ' ')
            : value;
    }

    handleSubmit(e, type) {
        e.preventDefault();
        
        if (type === 'makeModel') {
            this.navigateToNextStep(e.target);
            this.updateFinalDisplay('[data-show-final-selected-vehicle]', 
                `${this.selectedData.make} ${this.selectedData.model}`);

            let make = this.selectedData.make;
            let model = this.selectedData.model
            // Get matching years and limit to 5, already sorted newest first
            const matchingYear = this.getMatchingOptions('year', { make, model }).slice(0, 5);

            if (matchingYear.length > 0) {
                this.navigateToNextStep(e.target);
                this.populateSuggestions('[data-year-suggestion]', matchingYear, 'year');
            }
        } else if (type === 'year') {
            this.finalizeVehicleSelection();
        }
    }

    populateSuggestions(selector, items, type) {
        const container = this.querySelector(selector);
        const suggestionBox = this.querySelector(`[data-${type}-suggestion-box]`);
        
        if (!container || !suggestionBox) return;
        
        this.showElement(suggestionBox);
        container.innerHTML = '';
        
        items.forEach(item => {
            const li = this.createElement('li', item, {
                [`data-${type}-value`]: '',
                'data-vehicle-year-value': this.selectedData.year,
                'data-vehicle-make-model': `${this.selectedData.make} | ${this.selectedData.model}`,
                [`data-vehicle-${type}-value`]: item,
                class: 'block__vehicle-lookup-input-result-li'
            });

            if (type === 'year') {
                li.addEventListener('click', (e) => {
                    container.querySelectorAll("li").forEach((list) => {
                        list.classList.remove("selected");
                    });
                    e.target.classList.add("selected");
                    var YearValue = e.target.textContent;

                    this.selectedData.year = YearValue;

                    e.target.closest(".block__vehicle-lookup-input-box").querySelector('[data-search-vehicle-year]').value = YearValue;
                    e.target.closest(".block__vehicle-lookup-input-box").querySelector('[data-search-vehicle-year-submit]').classList.remove("d-none");
                });
            }
                        
            container.appendChild(li);
        });
    }

    getMatchingOptions(field, filters) {
        const vehicleArray = Object.values(this.sidebtsideJsonData);
        const fieldMap = { year: 'year' };
        const actualField = fieldMap[field] || field;
        
        const matching = vehicleArray
            .filter(v => 
                Object.entries(filters).every(([key, value]) => {
                    const vehicleValue = (key === 'year' || key === 'Year') ? v.year : v[key];
                    return vehicleValue === value;
                })
            )
            .map(v => v[actualField])
            .filter(value => value && value.trim() !== '');
        
        const uniqueValues = [...new Set(matching)];
        
        // Sort years in descending order (newest first) if we're dealing with years
        if (field === 'year') {
            return uniqueValues.sort((a, b) => parseInt(b) - parseInt(a));
        }
        
        return uniqueValues;
    }

    // Utility methods
    normalizeQuery(value) {
        return value.toLowerCase().replace(/\s+/g, ' ').trim();
    }

    filterMakeModels(query) {
        return this.uniqueMakeModels.filter(item => {
            const normalizedItem = item.toLowerCase()
                .replace(/\s*\|\s*/g, ' ')
                .replace(/\s+/g, ' ')
                .trim();
            return normalizedItem.includes(query);
        });
    }

    getUniqueYears(make, model) {
        const vehicleArray = Object.values(this.sidebtsideJsonData);
        const matchingYears = vehicleArray
            .filter(v => v.make === make && v.model === model)
            .map(v => v.year)
            .filter(Boolean);
        
        // Sort years in descending order (newest first)
        return [...new Set(matchingYears)].sort((a, b) => parseInt(b) - parseInt(a));
    }

    populateResults(container, resultBox, matches, type) {
        this.showElement(resultBox);
        
        if (matches.length > 0) {
            const attributeMap = {
                makeModel: { 'data-vehicle-model-value': '', 'data-vehicle-value': '' },
                year: { 'data-year-value': '', 'data-vehicle-year-value': '' }
            };

            container.innerHTML = matches.map(match => {
                const baseAttrs = attributeMap[type];
                const attrs = { ...baseAttrs };
                
                if (type === 'makeModel') {
                    attrs['data-vehicle-model-value'] = match;
                } else if (type === 'year') {
                    attrs['data-vehicle-year-value'] = match;
                    attrs['data-vehicle-make-model'] = `${this.selectedData.make} | ${this.selectedData.model}`;
                }

                const displayValue = type === 'makeModel' ? match.replace(/\s*\|\s*/g, ' ') : match;
                return this.createListItem(displayValue, attrs);
            }).join('');
        } else {
            container.innerHTML = '<li class="data-vehicle-model-list">No results found</li>';
        }
    }

    showMinLengthMessage(container, box, minLength, type) {
        this.showElement(box);
        const itemType = type === 'makeModel' ? 'vehicle' : type;

        if(type == "year"){
            container.innerHTML = `<li class="data-vehicle-model-list">Please enter at least ${minLength} number${minLength > 1 ? 's' : ''} of the ${itemType}.</li>`;
        }else{
            container.innerHTML = `<li class="data-vehicle-model-list">Please enter at least ${minLength} character${minLength > 1 ? 's' : ''} of the ${itemType}.</li>`;
        }
        
    }

    updateInputAndUI(targetElement, value, type) {
        const inputMap = {
            makeModel: this.elements.makeModelInput,
            year: this.elements.yearInput
        };
        
        const submitMap = {
            makeModel: this.elements.submitButton,
            year: this.elements.yearSubmitButton
        };
        
        const boxMap = {
            makeModel: this.elements.makeModelBox,
            year: this.elements.yearBox
        };

        const input = inputMap[type];
        const submit = submitMap[type];
        const box = boxMap[type];

        if (input) input.value = value;
        if (submit) this.toggleSubmitButton(submit, true);
        if (box) this.hideElement(box);

        // Update selection state
        const container = targetElement.closest('.block__vehicle-lookup-input-box');
        container?.querySelectorAll('li').forEach(li => li.classList.remove('selected'));
        targetElement.classList.add('selected');
    }

    navigateToNextStep(element) {
        const currentStep = element.closest('.block__vehicle-lookup-popup-details');
        const nextStepIndex = currentStep.getAttribute('data-next-step');
        const parentEle = currentStep.closest('.block__vehicle-lookup-popup-list');
        
        parentEle.querySelectorAll('.block__vehicle-lookup-popup-details')
            .forEach(step => this.hideElement(step));
        
        const nextStep = parentEle.querySelector(`[current-step="${nextStepIndex}"]`);
        if (nextStep) this.showElement(nextStep);
    }

    updateFinalDisplay(selector, content) {
        const element = this.querySelector(selector);
        if (element) element.innerHTML = content;
    }

    finalizeVehicleSelection() {
        document.querySelector(".block__vehicle-lookup-popup:not(.d-none) .block__vehicle-lookup-popup-details:not(.d-none)").querySelector(".block__vehicle-lookup-input-button").classList.add("loading");
        const { year, make, model } = this.selectedData;
        
        // Always create/update vehicle data to ensure it's set as default
        const vehicleData = this.createVehicleData(year, make, model);
        this.addVehicleToSelection(vehicleData);

        const partNumbers = this.getPartNumbers(year, make, model);
        this.redirectToResults(partNumbers);
    }

    checkIfVehicleExists(year, make, model) {
        return window.customerSelectedVehicles?.sidebyside?.some(vehicle =>
            vehicle.year === year &&
            vehicle.make === make &&
            vehicle.model === model
        ) || false;
    }

    createVehicleData(year, make, model) {
        return {
            id: Math.floor(Math.random() * 4000) + 1000,
            year,
            make,
            model,
            default: true
        };
    }

    resetAllVehicleDefaults() {
        const storageKeys = [
            'vehicle_data_side',
            'vehicle_data_sahdt',
            'vehicle_data',
            'turf_data',
            'heavy_equipment_data'
        ];

        storageKeys.forEach(key => {
            try {
                const storedData = localStorage.getItem(key);
                if (storedData) {
                    const vehicles = JSON.parse(storedData);
                    if (Array.isArray(vehicles)) {
                        vehicles.forEach(vehicle => {
                            vehicle.default = false;
                        });
                        localStorage.setItem(key, JSON.stringify(vehicles));
                    }
                }
            } catch (error) {
                console.error(`Error updating ${key}:`, error);
            }
        });
    }

    addVehicleToSelection(vehicleData) {
        const isCustomer = document.body.classList.contains('customer-loggedin');
        
        // Reset all vehicle defaults to false across all storage keys
        this.resetAllVehicleDefaults();
        
        // Set the new vehicle as default: true
        vehicleData.default = true;
        
        if (!window.customerSelectedVehicles) {
            window.customerSelectedVehicles = { sidebyside: [] };
        }
        if (!window.customerSelectedVehicles.sidebyside) {
            window.customerSelectedVehicles.sidebyside = [];
        }

        // Check if vehicle already exists, if so update it, otherwise add it
        const existingIndex = window.customerSelectedVehicles.sidebyside.findIndex(
            v => v.year === vehicleData.year && 
                 v.make === vehicleData.make && 
                 v.model === vehicleData.model
        );

        if (existingIndex !== -1) {
            window.customerSelectedVehicles.sidebyside[existingIndex] = vehicleData;
        } else {
            window.customerSelectedVehicles.sidebyside.push(vehicleData);
        }
        
        // Update currentSelectedData and currentSelectedDataLabels with latest vehicle
        localStorage.setItem('currentSelectedData', JSON.stringify(vehicleData));
        localStorage.setItem('currentSelectedDataLabels', JSON.stringify(vehicleData));

        if (isCustomer && window.shopifyFormUtility) {
            localStorage.removeItem('vehicle_data_side');
        } else {
            localStorage.setItem('vehicle_data_side', JSON.stringify(window.customerSelectedVehicles.sidebyside));
        }
    }

    getPartNumbers(year, make, model) {
        const vehicleArray = Object.values(this.sidebtsideJsonData);
        const filteredVehicles = vehicleArray.filter(item => 
            String(item.year || '').toLowerCase() === String(year || '').toLowerCase() &&
            String(item.make || '').toLowerCase() === String(make || '').toLowerCase() &&
            String(item.model || '').toLowerCase() === String(model || '').toLowerCase()
        );

        const uniquePartNumbers = [...new Set(
            filteredVehicles
                .map(item => item.part_number)
                .filter(Boolean)
        )];
        
        return uniquePartNumbers.map(p => `sku-${p}`).join(',');
    }

    redirectToResults(partNumbers) {
        const isCustomer = document.body.classList.contains('customer-loggedin');
        
        if (isCustomer && window.shopifyFormUtility) {
            shopifyFormUtility._updateCustomerSideMeta(window.customerSelectedVehicles.sidebyside, partNumbers);
        } else {
            window.location.href = `/collections/all?filter.p.tag=${partNumbers}`;
        }
    }

    createElement(tag, textContent, attributes = {}) {
        const element = document.createElement(tag);
        if (textContent) element.textContent = textContent;
        
        Object.entries(attributes).forEach(([key, value]) => {
            if (key === 'class') {
                element.className = value;
            } else {
                element.setAttribute(key, value);
            }
        });
        
        return element;
    }

    // DOM utility methods
    createListItem(content, attributes = {}) {
        const attrs = Object.entries({
            ...attributes,
            class: attributes.class || 'data-vehicle-model-list'
        }).map(([k, v]) => `${k}="${v}"`).join(' ');
        
        return `<li ${attrs}>${content}</li>`;
    }

    showElement(element) {
        element?.classList.remove('d-none');
    }

    hideElement(element) {
        element?.classList.add('d-none');
    }

    toggleSubmitButton(button, show) {
        if (!button) return;
        button.classList.toggle('d-none', !show);
    }
}

customElements.define('side-by-side-shop', SideBySideShop);